/**
 * Phase 5 Component 25 — EBA scope resolver.
 *
 * Pure module that determines whether a given worker + shift falls within
 * the scope of an Enterprise Agreement (EBA). The rate-context resolver
 * uses this to pick the EBA-specific rate version when available, falling
 * back to the national default award.
 *
 * Scope filter JSON shape (eba_agreements.scope_filter):
 *
 *   {
 *     // Optional: restrict to specific facility IDs
 *     "facility_ids": ["fac-1", "fac-2"],
 *     // Optional: restrict to specific classification streams
 *     "classification_streams": ["direct_care","aged_care_employee"],
 *     // Optional: restrict to specific service areas (SAH branches)
 *     "service_area_ids": ["svc-1"],
 *     // Optional: restrict to specific classification levels
 *     "classification_levels": ["L3","RN_LEVEL_2"],
 *     // Optional: restrict to state-specific rates/rules
 *     "state_codes": ["VIC","NSW"],
 *     // Optional: restrict to specific employment types
 *     "employment_types": ["FULL_TIME","PART_TIME"],
 *     // Optional: restrict to specific staff IDs (eg site agreement carve-out)
 *     "staff_ids": ["staff-1"]
 *   }
 *
 * An empty filter ({}) means the EBA applies to ALL workers in the tenant
 * for the matching base_award_code. Multiple filter keys are AND-combined.
 * If any filter array is empty, that key is ignored.
 */

export interface EbaAgreement {
  id: string;
  tenantId: string;
  baseAwardCode: string;
  scopeFilter: Record<string, unknown>;
  effectiveFrom: string; // yyyy-mm-dd
  effectiveTo: string | null;
  status: string;
}

export interface EbaScopeQuery {
  tenantId: string;
  awardCode: string;
  shiftDate: string; // yyyy-mm-dd
  facilityId?: string | null;
  serviceAreaId?: string | null;
  classificationStream?: string | null;
  classificationLevel?: string | null;
  employmentType?: string | null;
  state?: string | null;
  staffId?: string | null;
}

export type EbaScopeDimension =
  | 'facilityId'
  | 'serviceAreaId'
  | 'classificationStream'
  | 'classificationLevel'
  | 'employmentType'
  | 'state'
  | 'staffId';

export interface EbaScopeResolution {
  eba: EbaAgreement | null;
  ambiguous: boolean;
  ambiguousEbaIds: string[];
  missingContextDimensions: EbaScopeDimension[];
}

const SCOPE_DIMENSIONS: Array<{
  filterKey: string;
  queryKey: EbaScopeDimension;
  normalize?: (value: string) => string;
}> = [
  { filterKey: 'facility_ids', queryKey: 'facilityId' },
  { filterKey: 'service_area_ids', queryKey: 'serviceAreaId' },
  { filterKey: 'classification_streams', queryKey: 'classificationStream' },
  { filterKey: 'classification_levels', queryKey: 'classificationLevel' },
  {
    filterKey: 'employment_types',
    queryKey: 'employmentType',
    normalize: (value) => value.toUpperCase(),
  },
  { filterKey: 'state_codes', queryKey: 'state', normalize: (value) => value.toUpperCase() },
  { filterKey: 'staff_ids', queryKey: 'staffId' },
];

function scopedValues(filter: Record<string, unknown>, key: string): string[] | null {
  const value = filter[key];
  if (!Array.isArray(value)) return null;
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
}

function normalizedQueryValue(
  query: EbaScopeQuery,
  dimension: (typeof SCOPE_DIMENSIONS)[number],
): string | null {
  const value = query[dimension.queryKey];
  if (typeof value !== 'string' || value.length === 0) return null;
  return dimension.normalize ? dimension.normalize(value) : value;
}

function normalizedFilterValues(
  filter: Record<string, unknown>,
  dimension: (typeof SCOPE_DIMENSIONS)[number],
): string[] | null {
  const values = scopedValues(filter, dimension.filterKey);
  if (!values || values.length === 0) return values;
  return dimension.normalize ? values.map(dimension.normalize) : values;
}

function baseEligibilityMatches(eba: EbaAgreement, query: EbaScopeQuery): boolean {
  if (eba.tenantId !== query.tenantId) return false;
  if (eba.baseAwardCode !== query.awardCode) return false;
  if (eba.status !== 'ACTIVE') return false;
  if (query.shiftDate < eba.effectiveFrom) return false;
  return true;
}

/** Returns true if the EBA applies to the worker+shift query. */
export function isEbaInScope(eba: EbaAgreement, query: EbaScopeQuery): boolean {
  if (!baseEligibilityMatches(eba, query)) return false;

  // Scope filters (all AND-combined; empty array = "no restriction").
  //
  // SAFETY: when an EBA has a non-empty filter array for a dimension AND the
  // query has no value for that dimension, the EBA does NOT match. This
  // prevents a facility-scoped EBA from leaking onto unscoped (e.g. home-
  // care) shifts, which would inflate pay. Empty filter arrays still mean
  // "no restriction".
  const filter = eba.scopeFilter ?? {};
  for (const dimension of SCOPE_DIMENSIONS) {
    const values = normalizedFilterValues(filter, dimension);
    if (values && values.length > 0) {
      const queryValue = normalizedQueryValue(query, dimension);
      if (!queryValue || !values.includes(queryValue)) return false;
    }
  }

  return true;
}

function specificity(eba: EbaAgreement): number {
  const filter = eba.scopeFilter ?? {};
  return SCOPE_DIMENSIONS.reduce((count, dimension) => {
    const values = scopedValues(filter, dimension.filterKey);
    return count + (values && values.length > 0 ? 1 : 0);
  }, 0);
}

function missingContextDimensions(
  candidates: EbaAgreement[],
  query: EbaScopeQuery,
): EbaScopeDimension[] {
  const missing = new Set<EbaScopeDimension>();
  for (const eba of candidates) {
    if (!baseEligibilityMatches(eba, query)) continue;
    const filter = eba.scopeFilter ?? {};
    for (const dimension of SCOPE_DIMENSIONS) {
      const values = normalizedFilterValues(filter, dimension);
      if (values && values.length > 0 && !normalizedQueryValue(query, dimension)) {
        missing.add(dimension.queryKey);
      }
    }
  }
  return Array.from(missing).sort();
}

export function resolveApplicableEbaStrict(
  candidates: EbaAgreement[],
  query: EbaScopeQuery,
): EbaScopeResolution {
  const missing = missingContextDimensions(candidates, query);
  if (missing.length > 0) {
    return { eba: null, ambiguous: false, ambiguousEbaIds: [], missingContextDimensions: missing };
  }
  const inScope = candidates.filter((eba) => isEbaInScope(eba, query));
  if (inScope.length === 0) {
    return { eba: null, ambiguous: false, ambiguousEbaIds: [], missingContextDimensions: missing };
  }
  if (inScope.length === 1) {
    return {
      eba: inScope[0]!,
      ambiguous: false,
      ambiguousEbaIds: [],
      missingContextDimensions: missing,
    };
  }

  const scored = inScope.map((eba) => ({ eba, specificity: specificity(eba) }));
  scored.sort((a, b) => {
    if (b.specificity !== a.specificity) return b.specificity - a.specificity;
    return b.eba.effectiveFrom.localeCompare(a.eba.effectiveFrom);
  });

  const best = scored[0]!;
  const sameSpecificityMatches = scored.filter((entry) => entry.specificity === best.specificity);
  if (sameSpecificityMatches.length > 1) {
    return {
      eba: null,
      ambiguous: true,
      ambiguousEbaIds: sameSpecificityMatches.map((entry) => entry.eba.id).sort(),
      missingContextDimensions: missing,
    };
  }

  return {
    eba: best.eba,
    ambiguous: false,
    ambiguousEbaIds: [],
    missingContextDimensions: missing,
  };
}

/**
 * Pick the most-specific applicable EBA from a list of candidates.
 *
 * "Most specific" = the EBA with the narrowest scope filter (most matching
 * keys). If multiple EBAs match equally, the most recently effective wins.
 *
 * Returns null if no EBA applies.
 */
export function resolveApplicableEba(
  candidates: EbaAgreement[],
  query: EbaScopeQuery,
): EbaAgreement | null {
  return resolveApplicableEbaStrict(candidates, query).eba;
}
